import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sortByName } from '../../../utils/sort';
import type { SettingsSupplierRow } from '../types';

interface UseManageSuppliersReturn {
  suppliers: SettingsSupplierRow[];
  archivedSuppliers: SettingsSupplierRow[];
  archivedLoaded: boolean;
  loadArchived: () => Promise<void>;
  addSupplier: (name: string, email: string) => Promise<boolean>;
  updateSupplier: (id: string, name: string, email: string) => Promise<boolean>;
  archiveSupplier: (id: string) => Promise<boolean>;
  unarchiveSupplier: (id: string) => Promise<boolean>;
  deleteSupplier: (id: string) => Promise<'ok' | 'has_machines' | 'error'>;
}

async function buildRows(data: SettingsSupplierRow[]): Promise<SettingsSupplierRow[]> {
  const ids = data.map((s) => s.id);
  if (!ids.length) return [];
  const { data: machineRows } = await supabase
    .from('machines')
    .select('supplier_id')
    .in('supplier_id', ids);
  const withMachines = new Set((machineRows ?? []).map((m) => m.supplier_id));
  return data.map((s) => ({ ...s, has_machines: withMachines.has(s.id) }));
}

export function useManageSuppliers(orgId: string | null): UseManageSuppliersReturn {
  const [suppliers, setSuppliers] = useState<SettingsSupplierRow[]>([]);
  const [archivedSuppliers, setArchivedSuppliers] = useState<SettingsSupplierRow[]>([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);

  const loadActive = async () => {
    if (!orgId) {
      setSuppliers([]);
      return;
    }
    try {
      const { data } = await supabase
        .from('suppliers')
        .select('*')
        .eq('organization_id', orgId)
        .eq('archived', false)
        .order('name');
      setSuppliers(await buildRows((data ?? []) as SettingsSupplierRow[]));
    } catch {
      setSuppliers([]);
    }
  };

  const refreshArchived = async (): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { data } = await supabase
        .from('suppliers')
        .select('*')
        .eq('organization_id', orgId)
        .eq('archived', true)
        .order('name');
      setArchivedSuppliers(await buildRows((data ?? []) as SettingsSupplierRow[]));
      return true;
    } catch {
      setArchivedSuppliers([]);
      return false;
    }
  };

  const loadArchived = async () => {
    if (!orgId || archivedLoaded) return;
    setArchivedLoaded(true);
    const ok = await refreshArchived();
    if (!ok) setArchivedLoaded(false);
  };

  useEffect(() => {
    setArchivedLoaded(false);
    setArchivedSuppliers([]);
    loadActive();
  }, [orgId]);

  const addSupplier = async (name: string, email: string): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { error } = await supabase.from('suppliers').insert({
        name,
        email: email.trim() || null,
        organization_id: orgId,
        archived: false,
      });
      if (error) throw error;
      await loadActive();
      return true;
    } catch {
      return false;
    }
  };

  const updateSupplier = async (id: string, name: string, email: string): Promise<boolean> => {
    try {
      const resolvedEmail = email.trim() || null;
      const { error } = await supabase.from('suppliers').update({ name, email: resolvedEmail }).eq('id', id);
      if (error) throw error;
      const update = (list: SettingsSupplierRow[]) =>
        sortByName(list.map((s) => (s.id === id ? { ...s, name, email: resolvedEmail } : s)));
      setSuppliers(update);
      setArchivedSuppliers(update);
      return true;
    } catch {
      return false;
    }
  };

  const archiveSupplier = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('suppliers').update({ archived: true }).eq('id', id);
      if (error) throw error;
      setSuppliers((prev) => prev.filter((s) => s.id !== id));
      if (archivedLoaded) await refreshArchived();
      return true;
    } catch { return false; }
  };

  const unarchiveSupplier = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('suppliers').update({ archived: false }).eq('id', id);
      if (error) throw error;
      setArchivedSuppliers((prev) => prev.filter((s) => s.id !== id));
      await loadActive();
      return true;
    } catch { return false; }
  };

  const deleteSupplier = async (id: string): Promise<'ok' | 'has_machines' | 'error'> => {
    try {
      const { count } = await supabase
        .from('machines')
        .select('*', { count: 'exact', head: true })
        .eq('supplier_id', id);
      if ((count ?? 0) > 0) return 'has_machines';
      const { error } = await supabase.from('suppliers').delete().eq('id', id);
      if (error) throw error;
      setSuppliers((prev) => prev.filter((s) => s.id !== id));
      setArchivedSuppliers((prev) => prev.filter((s) => s.id !== id));
      return 'ok';
    } catch {
      return 'error';
    }
  };

  return {
    suppliers,
    archivedSuppliers,
    archivedLoaded,
    loadArchived,
    addSupplier,
    updateSupplier,
    archiveSupplier,
    unarchiveSupplier,
    deleteSupplier,
  };
}
